import React, { Fragment, useEffect } from 'react';
import { Menu, Transition } from '@headlessui/react';
import {
	ChevronRightIcon,
	EllipsisHorizontalIcon,
	PencilSquareIcon,
	XMarkIcon,
} from '@heroicons/react/24/outline';
import moment from 'moment-timezone';

import { deleteEvent, getEvent } from '../api/events';
import { getModuleFields } from '../api/modules';

import MultiAssetHolder from '../components/Events_to_deprecate/MultiAssetHolder';
import {
	classNames,
	getUserFullName,
	pluralize,
} from '../components/helpers/functions';
import Modal from '../components/Shell/Modal';
import { AvatarGenerated } from '../components/Avatar';
import Badge from '../components/Badge';
import LoadingSkeleton from '../components/Shell/LoadingSkeleton';
import Pill from '../components/Pill';
import Button from '../components/Button';

import usePrevious from '../hooks/usePrevious';
import useAsync from '../hooks/useAsync';
import useModal from '../hooks/useModal';
import useSettings from '../hooks/useSettings';

const MODAL_NAME = 'eventDetails';
const EVENT_MODULE_ID = process.env.REACT_APP_MODULE_ID_EVENT;

export default () => {
	const {
		value: event,
		status,
		execute: refreshEvent,
	} = useAsync(getEvent, {
		immediate: false,
	});

	const { timezone } = useSettings();

	const { execute: onDeleteEvent } = useAsync(deleteEvent, {
		immediate: false,
	});

	const { value: moduleFields, status: isLoadingModuleFields } = useAsync(
		getModuleFields,
		{
			defaultPayload: { id: EVENT_MODULE_ID, active: true },
		}
	);

	const { open: options, setClose } = useModal('eventDetails');
	const { setOpen: onOpenContactModal } = useModal('viewContact');
	const { open: isServiceOverviewOpen, setOpen: onOpenServiceModal } =
		useModal('serviceOverview');
	const { setOpen: onOpenTicketModal } = useModal('ticketOverview');
	const { open: manageEventModalOptions, setOpen: onOpenManageEventModal } =
		useModal('eventAdd');

	const { open: isOpenCompleteTaskModal, setOpen: onOpenCompleteTaskModal } =
		useModal('taskCompletedModal');

	const { setOpen: onOpenDeleteModal, setClose: onCloseDeleteModal } =
		useModal('deleteModal');

	const prevServiceOverview = usePrevious(isServiceOverviewOpen);

	useEffect(() => {
		if (!isServiceOverviewOpen && prevServiceOverview) {
			setClose();
		}
	});

	useEffect(() => {
		if (options?.id) {
			refreshEvent({ id: options?.id });
		}
	}, [options?.id]);

	useEffect(() => {
		if (manageEventModalOptions) {
			setClose();
		}
	}, [manageEventModalOptions]);

	const date = event?.scheduledDate || event?.estimatedDate;
	const allDayEvent = !!event?.allDayFlags > 0;
	const mDate = allDayEvent ? moment(date).utc() : moment(date).tz(timezone);
	const eDate = event?.scheduledDateEnd
		? moment(event?.scheduledDateEnd).tz(timezone)
		: null;

	const availableVendors = [
		...(event?.service?.contacts?.length ? event?.service?.contacts : []),
		...(event?.contacts?.length ? event?.contacts : []),
	];

	const contacts = [...new Set(availableVendors.map((v) => v.id))].map((id) =>
		availableVendors.find((v) => v.id === id)
	);

	const assets = [...(event?.assets?.length ? event?.assets : [])];

	const CONVERTER = {
		'event-title': 'title',
		date: 'scheduledDate',
		'event-type': 'category',
	};

	const isOverdue =
		event?.type === 'task' &&
		mDate.isBefore(moment().tz(timezone).startOf('day'));

	return (
		<Modal
			name={MODAL_NAME}
			key={MODAL_NAME}
			size={'2xl'}
			noMargins
			showCloseIcon={false}
		>
			{status === 'pending' && !event ? (
				<LoadingSkeleton />
			) : (
				<>
					<div className="bg-stone-50 rounded-t-xl p-6 pl-8">
						<div className="flex justify-between items-center">
							<div className="text-lg font-semibold leading-6 text-gray-600 flex-1 flex items-center">
								<div
									className={classNames(
										'flex items-center justify-between flex-1',
										isOverdue ? 'text-red-700' : 'text-gray-600'
									)}
								>
									{`${isOverdue ? 'Overdue: ' : ''} ${event?.title}`}
								</div>

								{event?.service && !isServiceOverviewOpen ? (
									<Badge
										isBeta
										border
										status="info"
										className="mr-3 pl-3"
										onClick={() => {
											onOpenServiceModal({ id: event?.service?.id });
										}}
									>
										View Task{' '}
										<ChevronRightIcon className="w-3 h-3 text-blue-600 ml-2" />
									</Badge>
								) : null}
							</div>

							<div className="flex items-center">
								{!event?.service && (
									<Menu
										as="div"
										className="relative inline-block text-left ml-2"
									>
										<Menu.Button className="flex items-center rounded-full hover:text-gray-800">
											<span className="sr-only">Open options</span>
											<EllipsisHorizontalIcon className="w-8 h-8 text-gray-500 cursor-pointer pr-2" />
										</Menu.Button>
										<Transition
											as={Fragment}
											enter="transition ease-out duration-100"
											enterFrom="transform opacity-0 scale-95"
											enterTo="transform opacity-100 scale-100"
											leave="transition ease-in duration-75"
											leaveFrom="transform opacity-100 scale-100"
											leaveTo="transform opacity-0 scale-95"
										>
											<Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
												<div className="py-1">
													<Menu.Item>
														{({ active }) => (
															<a
																className={classNames(
																	active ? 'text-red-900' : 'text-red-700',
																	' block px-4 py-2 text-xs cursor-pointer'
																)}
																onClick={() => {
																	onOpenDeleteModal({
																		title:
																			'Are you sure you want to delete this event?',
																		description:
																			'Deleting this event will cause it to be removed from any asset history',
																		confirmText: 'Delete Event',
																		onConfirm: () =>
																			onDeleteEvent({ id: event?.id }, () => {
																				onCloseDeleteModal();
																				setClose();
																			}),
																	});
																}}
															>
																<span>Delete Event</span>
															</a>
														)}
													</Menu.Item>
												</div>
											</Menu.Items>
										</Transition>
									</Menu>
								)}
								{!event?.service && (
									<PencilSquareIcon
										className="w-6 h-6 text-gray-500 cursor-pointer pr-2"
										onClick={() => {
											onOpenManageEventModal({ id: event?.id });
										}}
									/>
								)}
								<XMarkIcon
									className="w-6 h-6 text-gray-500 cursor-pointer pr-2"
									onClick={() => setClose()}
								/>
							</div>
						</div>

						<div
							className={classNames(
								'text-xs leading-6 flex items-center text-gray-500'
							)}
						>
							{mDate?.get('h') !== 0 && eDate
								? `${mDate?.format('h:mmA')}${
										eDate ? ` to ${eDate?.format('h:mmA')} on ` : ''
								  }`
								: ''}
							{mDate?.format('MMMM Do, YYYY')}
						</div>
					</div>

					<div className="mx-8 py-6">
						<dl className="divide-y divide-gray-100">
							{event?.service ? (
								<>
									<div className="px-4 py-1.5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
										<dt className="text-xs font-medium leading-6 text-gray-900">
											Status
										</dt>
										<dd className="mt-1 text-xs leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
											{event?.status === 'COMPLETED' && (
												<Badge
													status={'success'}
													isBeta
													className="mt-1 text-xxs"
												>
													Completed
												</Badge>
											)}
											{event?.status === 'SKIPPED' && (
												<Badge
													status={'default'}
													isBeta
													className="mt-1 text-xxs"
												>
													Skipped
												</Badge>
											)}
											{event?.status === 'SCHEDULED' && (
												<Badge
													status={isOverdue ? 'error' : 'success'}
													isBeta
													className="mt-1 text-xxs"
												>
													Scheduled
												</Badge>
											)}
											{event?.status === 'ESTIMATED' && (
												<Badge
													status={isOverdue ? 'error' : 'default'}
													isBeta
													className="mt-1 text-xxs"
												>
													Estimated
												</Badge>
											)}
										</dd>
									</div>

									{event?.status === 'COMPLETED' && (
										<>
											<div className="px-4 py-1.5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
												<dt className="text-xs font-medium leading-6 text-gray-900">
													Completion Date
												</dt>
												<dd className="mt-1 text-xs leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
													{moment(event?.completedDate)
														.tz(timezone)
														.format('dddd, MMMM Do, YYYY')}{' '}
													<span className="text-gray-400 ml-2">
														{`(Estimated ${moment(event?.estimatedDate)
															.tz(timezone)
															.format('MM/DD/YYYY')})`}
													</span>
												</dd>
											</div>

											<div className="px-4 py-1.5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
												<dt className="text-xs font-medium leading-6 text-gray-900">
													Completed By
												</dt>
												<dd className="mt-1 text-xs leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
													<div className="flex items-center">
														<AvatarGenerated
															id={getUserFullName(event?.createdByUser)}
															type={'person'}
															size="xxs"
															className={'mr-2'}
														/>
														{getUserFullName(event?.createdByUser)}
													</div>
												</dd>
											</div>
										</>
									)}
								</>
							) : null}

							{moduleFields
								?.filter(
									(field) =>
										[
											'',
											'all-day-event',
											'start-time',
											'end-time',
											'event-title',
											'date',
										].indexOf(field?.key) === -1
								)
								?.map((field) => {
									const value =
										event && event[CONVERTER[field?.key] || field?.key];
									if (value) {
										return (
											<>
												<div className="px-4 py-1.5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
													<dt className="text-xs font-medium leading-6 text-gray-900">
														{field?.label}
													</dt>
													<dd className="mt-1 text-xs leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
														{field?.type === 'date'
															? moment(value)
																	.tz(timezone)
																	.format('dddd, MMM Do, YYYY')
															: field?.type === 'category'
															? value?.name
															: value}
													</dd>
												</div>
												{
													/* CUSTOM */ field?.type === 'date' && (
														<div className="px-4 py-1.5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
															<dt className="text-xs font-medium leading-6 text-gray-900">
																Time
															</dt>
															<dd className="mt-1 text-xs leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
																{mDate?.get('h') !== 0
																	? `${mDate.format('h:mmA')}${
																			eDate
																				? ` to ${eDate.format('h:mmA')}`
																				: ''
																	  }`
																	: 'All day'}
															</dd>
														</div>
													)
												}
											</>
										);
									}
								})}

							{assets?.length ? (
								<div className="px-4 py-1.5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
									<dt className="text-xs font-medium leading-6 text-gray-900">
										{pluralize(assets?.length, 'Asset', 'Assets')}
									</dt>
									<dd className="mt-1 text-xs leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
										{assets?.length ? (
											<div className="flex text-xs items-center">
												<MultiAssetHolder assets={assets} />
											</div>
										) : null}
									</dd>
								</div>
							) : null}

							{event?.users?.length ? (
								<div className="px-4 py-1.5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
									<dt className="text-xs font-medium leading-6 text-gray-900">
										{pluralize(
											event?.users?.length,
											'Team member',
											'Team members'
										)}
									</dt>
									<dd className="mt-1 text-xs leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
										<div className="flex flex-wrap">
											{event?.users?.length
												? event?.users?.map((user) => {
														return (
															<Pill>
																<div className="flex items-center">
																	<AvatarGenerated
																		id={getUserFullName(user)}
																		type={'person'}
																		size="xxs"
																		className={'mr-2'}
																	/>
																	{getUserFullName(user)}
																</div>
															</Pill>
														);
												  })
												: null}
										</div>
									</dd>
								</div>
							) : null}
							{contacts?.length ? (
								<div className="px-4 py-1.5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
									<dt className="text-xs font-medium leading-6 text-gray-900">
										{pluralize(contacts?.length, 'Vendor', 'Vendors')}
									</dt>
									<dd className="mt-1 text-xs leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
										<div className="flex flex-wrap">
											{contacts?.length ? (
												<>
													<ul className="divide-y divide-gray-100">
														{contacts?.map((contact) => {
															return (
																<li
																	className="flex items-center text-gray-500 py-1 cursor-pointer"
																	onClick={(e) => {
																		onOpenContactModal({ id: contact.id });
																	}}
																>
																	<div className="flex items-center">
																		<AvatarGenerated
																			id={
																				contact?.type === 'company'
																					? contact.id
																					: getUserFullName(contact)
																			}
																			type={contact?.type}
																			size="xxs"
																			className={'mr-2'}
																		/>
																		{contact?.type === 'company'
																			? contact.companyName
																			: getUserFullName(contact)}
																	</div>
																</li>
															);
														})}
													</ul>
												</>
											) : null}
										</div>
									</dd>
								</div>
							) : null}
							{event?.tickets?.length ? (
								<div className="px-4 py-1.5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
									<dt className="text-xs font-medium leading-6 text-gray-900">
										{pluralize(event?.tickets?.length, 'Ticket', 'Tickets')}
									</dt>
									<dd className="mt-1 text-xs leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
										<div className="flex flex-wrap">
											{event?.tickets?.length ? (
												<>
													{' '}
													<ul className="divide-y divide-gray-100">
														{event?.tickets?.map((ticket) => {
															return (
																<li
																	className="flex items-center text-gray-500 py-1.5"
																	onClick={() =>
																		onOpenTicketModal({ id: ticket?.id })
																	}
																>
																	<div className="flex items-center">
																		{ticket?.ticketCode}
																	</div>
																</li>
															);
														})}
													</ul>
												</>
											) : null}
										</div>
									</dd>
								</div>
							) : null}
						</dl>
						{event?.service &&
							event?.status !== 'SKIPPED' &&
							event?.status !== 'COMPLETED' && (
								<div className="flex justify-end mt-3">
									<Button
										onClick={() => {
											onOpenCompleteTaskModal({
												eventId: event?.id,
												service: event?.service,
												defaultForm: {
													completedDate: new Date(),
												},
												onClose: () => setClose(),
											});
										}}
									>
										Mark as Completed
									</Button>
								</div>
							)}
					</div>
				</>
			)}
		</Modal>
	);
};
